import { useParams } from "react-router-dom";
import Footer from "../layout/footer";
import Header from "../layout/header";
import { useEffect, useState } from "react";
import {
  extractFirstImageSrc,
  formatDateTimeWithDay,
  removeImageTags,
} from "../utils/common";
import { isEmpty } from "lodash";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import {
  FacebookShareButton,
  LineShareButton,
  LinkedinShareButton,
  TwitterShareButton,
} from "react-share";
import { LinkedinIcon, TwitterIcon, FacebookIcon } from "../icons";
import { Helmet } from "react-helmet";

export default function BlogDetailPage() {
  let { id } = useParams();
  const [loading, setLoading] = useState<boolean>(false);

  const [data, setData] = useState<{
    id: number;
    code: string;
    title: string;
    content: string;
    date: string;
  }>();

  const fetchBlog = async () => {
    setLoading(true);
    for (let attempt = 1; attempt <= 3; attempt++) {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BASE_URL}/${id}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to add row");
        }
        const result = await response.json();
        setData(result);
        break;
      } catch (error: any) {
        if (attempt === 3) {
          throw new Error(
            `Failed to fetch data after ${3} attempts: ${error.message}`
          );
        }
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchBlog();
  }, []);

  const pageUrl = `https://infinx-mobility.com/detail-blog/${id}`;
  const pageTitle = data?.title || "Article InfinX Mobility";
  const pageDescription = data?.content
    ? removeImageTags(data.content).substring(0, 160) + "..."
    : "Découvrez cet article sur InfinX Mobility";
  const pageImage = data?.content
    ? extractFirstImageSrc(data.content)
    : "https://infinx-mobility.com/assets/logo/logo.png";

  return (
    <main className="bg-white">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />

        {/* Facebook Open Graph */}
        <meta property="og:url" content={pageUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={pageImage!} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@InfinXMobility" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={pageImage!} />

        {/* LinkedIn */}
        <meta property="og:site_name" content="InfinX Mobility" />
      </Helmet>
      <main className="px-4 md:px-6 lg:px-8 xl:px-16 h-full w-full md:max-w-[100rem] 2xl:max-w-[2560px] md:mx-auto gap-6 font-dmsans bg-white py-4 md:py-6">
        <Header inBLog />
        <div className="bg-primary-40">
          {loading ? (
            <section className="max-w-3xl mx-auto rounded-2xl pt-8 flex flex-col gap-2">
              <div className="flex items-start">
                <a href="/#blog" className="flex items-center gap-">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M11.8 13.5999L8.19995 9.9999L11.8 6.3999"
                      stroke="#525252"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                  Blog
                </a>
              </div>
              <Skeleton count={2} />
              <h3 className="text-primary-55 font-light text-sm">
                <Skeleton />
              </h3>

              <h5 className="text-primary-50 font-semibold text-2xl md:text-4xl md:h-20">
                <Skeleton />
              </h5>
            </section>
          ) : !isEmpty(data) ? (
            <section className="max-w-3xl mx-auto rounded-2xl pb-24 pt-8 px-2 flex flex-col gap-2">
              <div className="flex items-start">
                <a href="/#blog" className="flex items-center gap-">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M11.8 13.5999L8.19995 9.9999L11.8 6.3999"
                      stroke="#525252"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                  Blog
                </a>
              </div>
              {extractFirstImageSrc(data!.content) ? (
                <img
                  src={extractFirstImageSrc(data!.content)!}
                  className="w-full h-96 bg-cover bg-no-repeat bg-center rounded-lg"
                  style={{ objectFit: "cover", borderRadius: "10px" }}
                />
              ) : (
                // <img
                //   src="/assets/logo/logo.png"
                //   className="w-full h-96 bg-cover"
                // />
                <></>
              )}
              <div className="flex items-center justify-between">
                <h3 className="text-primary-55 font-light text-sm">
                  {formatDateTimeWithDay(data!.date)}
                </h3>
                <div className="flex items-center gap-4">
                  <FacebookShareButton
                    url={pageUrl}
                    title={`${pageTitle} - ${pageDescription}`}
                    hashtag="#InfinXMobility"
                  >
                    <FacebookIcon />
                  </FacebookShareButton>

                  <TwitterShareButton
                    url={pageUrl}
                    title={pageTitle}
                    via="InfinXMobility"
                    hashtags={["mobility", "transport"]}
                  >
                    <TwitterIcon />
                  </TwitterShareButton>

                  <LinkedinShareButton
                    url={pageUrl}
                    title={pageTitle}
                    summary={pageDescription}
                    source="InfinX Mobility"
                  >
                    <LinkedinIcon />
                  </LinkedinShareButton>
                </div>
              </div>

              <h5 className="text-primary-50 font-semibold text-2xl md:text-4xl md:h-20">
                {data!.title}
              </h5>
            </section>
          ) : (
            <></>
          )}
        </div>
        {loading ? (
          <section className="max-w-3xl mx-auto rounded-2xl py-12 bg-white">
            <Skeleton />
          </section>
        ) : (
          <section className="max-w-3xl mx-auto rounded-2xl py-12 bg-white">
            <div
              dangerouslySetInnerHTML={{
                __html: data?.content!,
              }}
              className="font-light"
            ></div>
          </section>
        )}

        <Footer />
      </main>
    </main>
  );
}
